// src/components/Dashboard.js
import { CAT_ICONS, CATEGORIES, PRIORITY_COLORS, PRIORITY_LIGHT } from "../constants";

export default function Dashboard({ tasks, setSelectedTask, setView, setTaskFilter }) {
  const open = tasks.filter(t => !t.completed);
  const overdue = open.filter(t => t.dueDate && new Date(t.dueDate) < new Date());
  const urgent = open.filter(t => t.priority === "Urgent");
  const completed = tasks.filter(t => t.completed);
  const upcoming = open.filter(t => t.dueDate).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)).slice(0, 6);

  const goToDetail = (task) => { setSelectedTask(task); setView("detail"); };

  function goToFiltered(filter) {
    setTaskFilter(filter);
    setView("tasks");
  }

  return (
    <div className="fade-in">
      <div style={styles.statsGrid}>
        {[
          { label: "Open Tasks", value: open.length,      color: "#3b82f6", bg: "#eff6ff", filter: "open" },
          { label: "Overdue",    value: overdue.length,   color: "#ef4444", bg: "#fff1f2", filter: "overdue" },
          { label: "Urgent",     value: urgent.length,    color: "#f97316", bg: "#fff7ed", filter: "urgent" },
          { label: "Completed",  value: completed.length, color: "#22c55e", bg: "#f0fdf4", filter: "completed" },
        ].map(({ label, value, color, bg, filter }) => (
          <div key={label} onClick={() => goToFiltered(filter)}
            style={{ ...styles.statCard, background: bg, border: `2px solid ${color}22`, cursor: "pointer" }}>
            <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: 42, color, lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#a07850", letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 6 }}>{label}</div>
            <div style={{ fontSize: 10, color: color, fontWeight: 700, marginTop: 4, opacity: 0.7 }}>Tap to view</div>
          </div>
        ))}
      </div>

      <div style={styles.grid2}>
        <div className="card">
          <h3 style={styles.cardTitle}>Upcoming Tasks</h3>
          {upcoming.length === 0
            ? <EmptyState text="All caught up! No upcoming tasks." />
            : upcoming.map(t => <TaskRow key={t.id} task={t} onClick={() => goToDetail(t)} />)
          }
        </div>
        <div className="card">
          <h3 style={styles.cardTitle}>Progress by Category</h3>
          {CATEGORIES.filter(c => tasks.some(t => t.category === c)).map(cat => {
            const catTasks = tasks.filter(t => t.category === cat);
            const catDone = catTasks.filter(t => t.completed).length;
            const pct = catTasks.length ? (catDone / catTasks.length) * 100 : 0;
            return (
              <div key={cat} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, color: "#5c3a1e", marginBottom: 5 }}>
                  <span>{CAT_ICONS[cat]} {cat}</span>
                  <span style={{ color: "#a07850" }}>{catDone}/{catTasks.length}</span>
                </div>
                <div style={{ background: "#f5ead8", borderRadius: 6, height: 8 }}>
                  <div style={{ background: "linear-gradient(90deg,#f97316,#f59e0b)", height: 8, borderRadius: 6, width: `${pct}%`, transition: "width 0.6s ease" }} />
                </div>
              </div>
            );
          })}
          {CATEGORIES.every(c => !tasks.some(t => t.category === c)) && <EmptyState text="Add tasks to see your progress!" />}
        </div>
      </div>

      {overdue.length > 0 && (
        <div className="card" style={{ marginTop: 20, border: "2px solid #fecaca", background: "#fff5f5" }}>
          <h3 style={{ ...styles.cardTitle, color: "#dc2626" }}>Overdue — Needs Attention!</h3>
          {overdue.map(t => <TaskRow key={t.id} task={t} onClick={() => goToDetail(t)} showDate />)}
        </div>
      )}

      {tasks.length === 0 && (
        <div className="card" style={{ marginTop: 20, textAlign: "center", padding: 48, border: "2px dashed #f0d8b0" }}>
          <div style={{ fontSize: 60, marginBottom: 12 }}>🏡</div>
          <h3 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 26, color: "#8B4513", marginBottom: 8 }}>Welcome to Homie!</h3>
          <p style={{ color: "#a07850", fontWeight: 700, marginBottom: 20 }}>Track your home repairs, get AI guides, and shop for parts — all in one place.</p>
          <button className="btn-primary" onClick={() => setView("add")}>Add Your First Task</button>
        </div>
      )}
    </div>
  );
}

export function TaskRow({ task, onClick, showDate }) {
  return (
    <div className="task-row" onClick={onClick}>
      <span style={{ fontSize: 20, flexShrink: 0 }}>{CAT_ICONS[task.category]}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#2c1810" }}>{task.title}</div>
        <div style={{ fontSize: 12, color: "#a07850", fontWeight: 600, marginTop: 2 }}>
          {task.category}{task.dueDate ? ` · ${showDate ? "Due: " + task.dueDate : task.dueDate}` : ""}
          {task.recurrence && task.recurrence !== "None" ? ` · ${task.recurrence}` : ""}
        </div>
      </div>
      <span style={{ background: PRIORITY_LIGHT[task.priority], color: PRIORITY_COLORS[task.priority], padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>
        {task.priority}
      </span>
    </div>
  );
}

function EmptyState({ text }) {
  return <div style={{ textAlign: "center", padding: 24, color: "#c09870" }}><p style={{ fontWeight: 700 }}>{text}</p></div>;
}

const styles = {
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 },
  statCard: { borderRadius: 20, padding: "20px 16px", textAlign: "center", boxShadow: "0 4px 16px rgba(160,100,40,0.08)", transition: "transform 0.18s" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 },
  cardTitle: { fontFamily: "'Fredoka One', cursive", fontSize: 20, color: "#8B4513", marginBottom: 16 },
};
